import React, { useState } from "react";
import Field from "components/Field";
import { getOptions, getBaseUnits, getUnitOptions } from "utils/fields";
import { maxLength, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import Accordion from "components/Accordion";
import VentilatorStats from "./VentilatorStats";
import Actions from "../Actions";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

const charLim40 = maxLength(40);
const supplyFlowValidation = getValidation("ventSupply");
const exhaustFlowValidation = getValidation("ventExhaust");
const fanPowerValidation = getValidation("ventFanPower");
const operationScheduleValidation = getValidation("dryerOperationSchedule");

export default ({ accessor, isSupplemental, change, formName, onResetFlow }) => {
    const label = useStateSelector(`${accessor}.label`) || {};
    const { id: ventTypeId } = useStateSelector(`${accessor}.type`) || {};
    const supplyFlowValue = useStateSelector(`${accessor}.supplyFlowrate`);
    const exhaustFlowValue = useStateSelector(`${accessor}.exhaustFlowrate`);
    const fanPowerValue = useStateSelector(`${accessor}.fanPower1`);
    const supplyFlowUnits = useStateSelector(`${accessor}.supplyFlowrate_u`);
    const exhaustFlowUnits = useStateSelector(`${accessor}.exhaustFlowrate_u`);
    const fanPowerUnits = useStateSelector(`${accessor}.fanPower1_u`);
    const isDefaultFanpower = useStateSelector(`${accessor}.isDefaultFanpower`);

    const { id: operationScheduleId, value: operationScheduleValue } =
        useStateSelector(`${accessor}.operationSchedule`) || {};
    const operationScheduleUnits = useStateSelector(`${accessor}.operationSchedule_u`);

    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const contentOpen = useStateSelector(`model.contentOpen`) || false;

    return (
        <Accordion
            className={sharedClasses.accordion}
            heading={<span className={sharedClasses.heading}>{`${label}`}</span>}
            large
            stats={() => <VentilatorStats accessor={accessor} formName={formName} />}
            hoverActions={
                <Actions
                    label="Ventilator"
                    type="ventilator"
                    accessor={accessor}
                    isSupplemental={isSupplemental}
                    change={change}
                    formName={formName}
                    onResetFlow={onResetFlow}
                />
            }
            forceOpen={contentOpen}
        >
            <InputRow gridTemplate="2fr 2fr 1fr 1fr">
                <Field
                    component={Input}
                    name={`${accessor}.manufacturer`}
                    type="text"
                    label="Manufacturer"
                    placeholder=""
                    customValidateFunc={charLim40}
                />
                <Field
                    component={Input}
                    name={`${accessor}.model`}
                    type="text"
                    label="Model"
                    placeholder=""
                    customValidateFunc={charLim40}
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.isEnergyStar`}
                    label="Energy Star"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.isHVICertified`}
                    label="HVI Certified"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.supplyFlowrate`}
                    label="Supply Air Flow Rate"
                    placeholder={0.0}
                    validate={supplyFlowValidation}
                    decimals={getDecimalPlaces("ventSupply")}
                    change={change}
                    setValue={supplyFlowValue}
                    units={{
                        base: getBaseUnits("ventSupply", modelUnits),
                        options: getUnitOptions("airFlow"),
                        selected: supplyFlowUnits,
                        unitType: "airFlow",
                        accessor: `${accessor}.supplyFlowrate_u`,
                    }}
                    disabled={ventTypeId === 3 || ventTypeId === 2}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.exhaustFlowrate`}
                    label="Exhaust Air Flow Rate"
                    placeholder={0.0}
                    validate={exhaustFlowValidation}
                    decimals={getDecimalPlaces("ventExhaust")}
                    change={change}
                    setValue={exhaustFlowValue}
                    units={{
                        base: getBaseUnits("ventExhaust", modelUnits),
                        options: getUnitOptions("airFlow"),
                        selected: exhaustFlowUnits,
                        unitType: "airFlow",
                        accessor: `${accessor}.exhaustFlowrate_u`,
                    }}
                    disabled={false}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.fanPower1`}
                    label="Fan Power"
                    placeholder={0.0}
                    validate={fanPowerValidation}
                    decimals={getDecimalPlaces("ventFanPower")}
                    change={change}
                    setValue={fanPowerValue}
                    units={{
                        base: getBaseUnits("ventFanPower", modelUnits),
                        options: getUnitOptions("power"),
                        selected: fanPowerUnits,
                        unitType: "power",
                        accessor: `${accessor}.fanPower1_u`,
                    }}
                    disabled={isDefaultFanpower}
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.isDefaultFanpower`}
                    label="Default Fan Power"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
            </InputRow>
            {isSupplemental && (
                <>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={Select}
                            name={`${accessor}.operationSchedule`}
                            options={[
                                ...getOptions({ fieldKey: "dryerOperationSchedule" }),
                                {
                                    label: "User specified",
                                    value: {
                                        id: 4,
                                        value: operationScheduleValue,
                                    },
                                },
                            ]}
                            label="Operation Schedule"
                            placeholder="Choose Operation Schedule"
                            validate={operationScheduleValidation}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.operationSchedule.value`}
                            label="Daily Operation"
                            placeholder={0.0}
                            disabled={operationScheduleId !== 4}
                            validate={operationScheduleValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("dryerOperationSchedule")}
                            change={change}
                            setValue={operationScheduleValue}
                            units={{
                                base: getBaseUnits("dryerOperationSchedule", modelUnits),
                                options: getUnitOptions("time"),
                                selected: operationScheduleUnits,
                                unitType: "time",
                                accessor: `${accessor}.operationSchedule_u`,
                            }}
                        />
                    </InputRow>
                </>
            )}
        </Accordion>
    );
};
